import {CSSProperties, FC, RefObject} from 'react'
import clsx from 'clsx'
import {InvoicePrintLayoutSettings, InvoicePrintModel} from '../../../invoice-print/core/_models'
import {PrintColumn} from '../../../invoice-print/core/printColumns'
import {ACCENT_COLORS, LEGIBLE_SIGNATURE_MM} from '../../../invoice-print/core/printLayoutSettings'
import {notices, RowsTotals, TotalRow, watermark} from '../../../invoice-print/core/printModel'
import {PagePart} from '../../../invoice-print/core/printPagination'
import {Watermark} from '../../../invoice-print/components/PrintBlocks'
import {
  BlockFrame,
  ClassicFoot,
  ClassicHeadCompact,
  ClassicHeadFull,
  ClassicItemsTable,
  ClassicNotes,
  ClassicNotices,
  ClassicParties,
  ClassicPattern,
  ClassicPayments,
  ClassicSignatures,
  ClassicTotals,
} from './ClassicBlocks'
import {ClassicField, ClassicNoteLine, hasPatternBlock, notesTitle} from './classicPrintModel'

// The earlier print page's A4 landscape sheets and the copy usePrintLayout measures. Both use the
// A4 page's sheet frame (.ip-sheet, .ip-sheet__inner/head/body/foot: the size and margins the
// pagination counts on) and the earlier page's own parts inside it.

export type ClassicShared = {
  invoice: InvoicePrintModel
  columns: PrintColumn[]
  noteLines: ClassicNoteLine[]
  patternFields: ClassicField[]
  grand: Record<string, string | null>
  extraRows: TotalRow[]
  settings: InvoicePrintLayoutSettings
  qrValue: string
  qrLogo: string
  site: string
  showSignature: boolean
  fontPt: number
  tight: boolean
}

const sheetClass = (shared: ClassicShared) =>
  clsx('ip-sheet ip-sheet--landscape cp-sheet', {'cp-sheet--tight': shared.tight})

/**
 * The template's colour («رنگ قالب»), as on the new page: the titles take its ink, and the grey
 * fills (column heads, the «فروشنده / خریدار» labels, the continuation header) take its soft tint.
 * The black lines and the page's composition stay the earlier page's own.
 */
export const sheetStyle = (shared: Pick<ClassicShared, 'fontPt' | 'settings'>) =>
  ({
    '--ip-font': `${shared.fontPt}pt`,
    '--cp-logo-mm': `${shared.settings.logoHeightMm}mm`,
    '--cp-primary': ACCENT_COLORS[shared.settings.accent].ink,
    '--cp-fill': ACCENT_COLORS[shared.settings.accent].soft,
  } as CSSProperties)

/** The stamp's height: the template's, and in the compact form never below what can be read. */
export const signatureHeightMm = (settings: InvoicePrintLayoutSettings, compact?: boolean) =>
  compact ? Math.min(settings.signatureHeightMm, LEGIBLE_SIGNATURE_MM) : settings.signatureHeightMm

const Signatures: FC<{shared: ClassicShared; compact?: boolean}> = ({shared, compact}) => (
  <ClassicSignatures
    invoice={shared.invoice}
    showSignature={shared.showSignature}
    heightMm={signatureHeightMm(shared.settings, compact)}
    buyerBox={shared.settings.showBuyerSignatureBox}
    compact={compact}
  />
)

/**
 * Every part, each in its own frame, at the sheet's width: what the layout measures. Hidden from view
 * and from print (.ip-measure-host).
 */
export const ClassicMeasure: FC<{
  shared: ClassicShared
  measureRef: RefObject<HTMLDivElement>
  /** The sums over every row: the widest values the page totals can hold. */
  allRowsTotals: RowsTotals
  /** The frozen column widths once the rows fit; until then the browser lays the table out. */
  widths: number[] | null
  /** Totals the columns must also hold: the sheets' page and running totals, and «جمع کل». */
  probeTotals: Record<string, string | null>[]
}> = ({shared, measureRef, allRowsTotals, widths, probeTotals}) => {
  const {invoice, columns, noteLines} = shared
  const frozen = widths && widths.length ? widths : undefined
  return (
    <div className='ip-measure-host'>
      <div className='ip-measure' aria-hidden='true' ref={measureRef}>
        <div className={sheetClass(shared)} style={sheetStyle(shared)}>
          <div className='ip-sheet__inner'>
            <div className='ip-sheet__head' data-measure-block='head-full'>
              <ClassicHeadFull
                invoice={invoice}
                qrValue={shared.qrValue}
                qrLogo={shared.qrLogo}
                qrId='cp-qr-measure'
              />
            </div>
            <div className='ip-sheet__head' data-measure-block='head-compact'>
              <ClassicHeadCompact invoice={invoice} />
            </div>
            <div className='ip-sheet__foot' data-measure-block='foot'>
              <ClassicFoot invoice={invoice} page={88} pages={88} site={shared.site} />
            </div>
            <div className='ip-sheet__body ip-sheet__body--measure'>
              <BlockFrame measureKey='parties'>
                <ClassicParties invoice={invoice} />
              </BlockFrame>
              {hasPatternBlock(shared.patternFields, invoice) && (
                <BlockFrame measureKey='pattern'>
                  <ClassicPattern invoice={invoice} fields={shared.patternFields} />
                </BlockFrame>
              )}
              {invoice.items.length > 0 && (
                <BlockFrame measureKey='items'>
                  <ClassicItemsTable
                    invoice={invoice}
                    columns={columns}
                    widths={frozen}
                    from={0}
                    to={invoice.items.length}
                    totals={allRowsTotals}
                    probeTotals={probeTotals}
                  />
                </BlockFrame>
              )}
              <BlockFrame measureKey='totals'>
                <ClassicTotals
                  invoice={invoice}
                  columns={columns}
                  widths={frozen}
                  grand={shared.grand}
                  extraRows={shared.extraRows}
                />
              </BlockFrame>
              {invoice.payments.length > 0 && (
                <BlockFrame measureKey='payments'>
                  <ClassicPayments invoice={invoice} from={0} to={invoice.payments.length} />
                </BlockFrame>
              )}
              {noteLines.length > 0 && (
                <BlockFrame measureKey='notes'>
                  <ClassicNotes
                    lines={noteLines}
                    title={notesTitle(noteLines)}
                    from={0}
                    to={noteLines.length}
                  />
                </BlockFrame>
              )}
              {notices(invoice).length > 0 && (
                <BlockFrame measureKey='notices'>
                  <ClassicNotices invoice={invoice} />
                </BlockFrame>
              )}
              <BlockFrame measureKey='signatures'>
                <Signatures shared={shared} />
              </BlockFrame>
              <BlockFrame measureKey='signatures-compact'>
                <Signatures shared={shared} compact />
              </BlockFrame>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const renderPart = (
  part: PagePart,
  shared: ClassicShared,
  widths: number[],
  totals: RowsTotals | undefined
) => {
  const {invoice, columns, noteLines} = shared
  if (part.kind === 'block') {
    switch (part.key) {
      case 'parties':
        return <ClassicParties invoice={invoice} />
      case 'pattern':
        return <ClassicPattern invoice={invoice} fields={shared.patternFields} />
      case 'totals':
        return (
          <ClassicTotals
            invoice={invoice}
            columns={columns}
            widths={widths}
            grand={shared.grand}
            extraRows={shared.extraRows}
          />
        )
      case 'notices':
        return <ClassicNotices invoice={invoice} />
      case 'signatures':
        return <Signatures shared={shared} compact={part.compact} />
      default:
        return null
    }
  }

  switch (part.key) {
    case 'items':
      return (
        <ClassicItemsTable
          invoice={invoice}
          columns={columns}
          widths={widths}
          from={part.from}
          to={part.to}
          totals={totals}
        />
      )
    case 'payments':
      return (
        <ClassicPayments
          invoice={invoice}
          from={part.from}
          to={part.to}
          continued={part.from > 0}
        />
      )
    case 'notes':
      return (
        <ClassicNotes
          lines={noteLines}
          title={notesTitle(noteLines)}
          from={part.from}
          to={part.to}
          continued={part.from > 0}
        />
      )
    default:
      return null
  }
}

/** The A4 landscape sheets, as they are shown and printed. */
export const ClassicSheets: FC<{
  shared: ClassicShared
  pages: PagePart[][]
  widths: number[]
  rowsTotals: Map<number, RowsTotals> | null
  sheetsRef?: RefObject<HTMLDivElement>
  style?: CSSProperties
}> = ({shared, pages, widths, rowsTotals, sheetsRef, style}) => {
  const mark = watermark(shared.invoice)
  return (
    <div className='cp-sheets' data-classic-sheets='' ref={sheetsRef} style={style}>
      {pages.map((parts, pageIndex) => (
        <section
          key={pageIndex}
          className={sheetClass(shared)}
          style={sheetStyle(shared)}
          aria-label={`صفحهٔ ${pageIndex + 1} از ${pages.length}`}
        >
          <Watermark text={mark} />
          <div className='ip-sheet__inner'>
            <div className='ip-sheet__head'>
              {pageIndex === 0 ? (
                <ClassicHeadFull
                  invoice={shared.invoice}
                  qrValue={shared.qrValue}
                  qrLogo={shared.qrLogo}
                  qrId={`cp-qr-${pageIndex}`}
                />
              ) : (
                <ClassicHeadCompact invoice={shared.invoice} />
              )}
            </div>
            <div className='ip-sheet__body'>
              {parts.map((part, partIndex) => (
                <BlockFrame key={`${part.key}-${partIndex}`}>
                  {renderPart(part, shared, widths, rowsTotals?.get(pageIndex))}
                </BlockFrame>
              ))}
            </div>
            <div className='ip-sheet__foot'>
              <ClassicFoot
                invoice={shared.invoice}
                page={pageIndex + 1}
                pages={pages.length}
                site={shared.site}
              />
            </div>
          </div>
        </section>
      ))}
    </div>
  )
}
